/**
 * useSubscriptionGate Hook
 * Custom hook for checking subscription entitlements before actions
 */

import { useCallback } from 'react';
import { useSubscription } from '../contexts/SubscriptionContext';

/**
 * Hook to check if a subscription-gated action is allowed
 * Returns: { allowed, showUpgrade, limit, usage }
 */
const useSubscriptionGate = () => {
  const {
    canAddMember,
    canCreateEvent,
    canUseRelationshipType,
    hasFeature,
    usage,
    limits,
    promptUpgrade
  } = useSubscription();

  /**
   * Check if user can perform a specific action
   * @param {string} action - The action to check (e.g., 'addMember', 'createEvent', 'useRelationshipType')
   * @param {object} options - Additional options (e.g., { relationshipTypeId: 'sports_team' })
   * @returns {object} - { allowed: boolean, reason: string, upgrade: function }
   */
  const checkAccess = useCallback((action, options = {}) => {
    switch (action) {
      case 'addMember':
        const canAdd = canAddMember();
        return {
          allowed: canAdd,
          reason: canAdd ? null : `You've reached the member limit (${limits.members})`,
          limit: limits.members,
          usage: usage.members,
          upgrade: () => promptUpgrade({ type: 'members' })
        };

      case 'createEvent':
        const canCreate = canCreateEvent();
        return {
          allowed: canCreate,
          reason: canCreate ? null : `You've reached the event limit (${limits.events})`,
          limit: limits.events,
          usage: usage.events,
          upgrade: () => promptUpgrade({ type: 'events' })
        };

      case 'useRelationshipType':
        if (!options.relationshipTypeId) {
          throw new Error('relationshipTypeId is required for useRelationshipType check');
        }
        const canUse = canUseRelationshipType(options.relationshipTypeId);
        return {
          allowed: canUse,
          reason: canUse ? null : 'This relationship type requires a premium plan',
          relationshipTypeId: options.relationshipTypeId,
          upgrade: () => promptUpgrade({
            type: 'relationshipType',
            relationshipTypeId: options.relationshipTypeId
          })
        };

      case 'useFeature':
        if (!options.featureName) {
          throw new Error('featureName is required for useFeature check');
        }
        const hasIt = hasFeature(options.featureName);
        return {
          allowed: hasIt,
          reason: hasIt ? null : `This feature requires a premium plan`,
          featureName: options.featureName,
          upgrade: () => promptUpgrade({
            type: 'feature',
            featureName: options.featureName
          })
        };

      default:
        throw new Error(`Unknown action: ${action}`);
    }
  }, [canAddMember, canCreateEvent, canUseRelationshipType, hasFeature, usage, limits, promptUpgrade]);

  /**
   * Guard function that throws or shows upgrade prompt if action is not allowed
   * @param {string} action - The action to guard
   * @param {object} options - Additional options
   * @returns {boolean} - true if allowed, otherwise shows upgrade prompt
   */
  const guardAction = useCallback((action, options = {}) => {
    const access = checkAccess(action, options);
    if (!access.allowed) {
      access.upgrade();
      return false;
    }
    return true;
  }, [checkAccess]);

  /**
   * Get usage summary for display
   */
  const getUsageSummary = useCallback(() => {
    return {
      members: {
        current: usage.members,
        limit: limits.members,
        percentage: limits.members >= 999999 ? 0 : (usage.members / limits.members) * 100,
        unlimited: limits.members >= 999999
      },
      events: {
        current: usage.events,
        limit: limits.events,
        percentage: limits.events >= 999999 ? 0 : (usage.events / limits.events) * 100,
        unlimited: limits.events >= 999999
      },
      relationshipTypes: {
        current: usage.relationshipTypes?.length || 0,
        limit: limits.relationshipTypes,
        percentage: limits.relationshipTypes >= 999999 ? 0 : ((usage.relationshipTypes?.length || 0) / limits.relationshipTypes) * 100,
        unlimited: limits.relationshipTypes >= 999999
      }
    };
  }, [usage, limits]);

  return {
    checkAccess,
    guardAction,
    getUsageSummary,
    // Convenience methods
    canAddMember,
    canCreateEvent,
    canUseRelationshipType,
    hasFeature
  };
};

export default useSubscriptionGate;
